import * as z from 'zod';

export const CreateCatalogImportRequest = /*#__PURE__*/ z.object({
  catalogImport: /*#__PURE__*/ z
    .object({
      _id: /*#__PURE__*/ z
        .string()
        .describe('CatalogImport ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      _createdDate: /*#__PURE__*/ z
        .date()
        .describe('Date and time the CatalogImport was created.')
        .optional()
        .nullable(),
    })
    .describe('CatalogImport to be created.'),
});
export const CreateCatalogImportResponse = /*#__PURE__*/ z.object({
  _id: /*#__PURE__*/ z
    .string()
    .describe('CatalogImport ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  _createdDate: /*#__PURE__*/ z
    .date()
    .describe('Date and time the CatalogImport was created.')
    .optional()
    .nullable(),
});
export const AddProductsToImportRequest = /*#__PURE__*/ z.object({
  catalogImportId: /*#__PURE__*/ z
    .string()
    .describe('Catalog Import ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  products: /*#__PURE__*/ z
    .array(
      /*#__PURE__*/ z.object({
        product: /*#__PURE__*/ z
          .intersection(
            /*#__PURE__*/ z.object({
              _id: /*#__PURE__*/ z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: /*#__PURE__*/ z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
                )
                .optional()
                .nullable(),
              name: /*#__PURE__*/ z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: /*#__PURE__*/ z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .min(1)
                .max(300)
                .optional()
                .nullable(),
              plainDescription: /*#__PURE__*/ z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: /*#__PURE__*/ z
                .boolean()
                .describe(
                  'Whether the product is visible to site visitors.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
              visibleInPos: /*#__PURE__*/ z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: /*#__PURE__*/ z
                .object({
                  main: /*#__PURE__*/ z
                    .intersection(
                      /*#__PURE__*/ z.object({
                        altText: /*#__PURE__*/ z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: /*#__PURE__*/ z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: /*#__PURE__*/ z
                          .enum(['IMAGE', 'VIDEO'])
                          .optional(),
                        thumbnail: /*#__PURE__*/ z
                          .object({
                            url: /*#__PURE__*/ z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: /*#__PURE__*/ z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: /*#__PURE__*/ z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: /*#__PURE__*/ z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: /*#__PURE__*/ z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      /*#__PURE__*/ z.intersection(
                        /*#__PURE__*/ z.xor([
                          /*#__PURE__*/ z.object({
                            _id: /*#__PURE__*/ z.never().optional(),
                            url: /*#__PURE__*/ z.never().optional(),
                          }),
                          /*#__PURE__*/ z.object({
                            url: /*#__PURE__*/ z.never().optional(),
                            _id: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          /*#__PURE__*/ z.object({
                            _id: /*#__PURE__*/ z.never().optional(),
                            url: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        /*#__PURE__*/ z.xor([
                          /*#__PURE__*/ z.object({
                            image: /*#__PURE__*/ z.never().optional(),
                            video: /*#__PURE__*/ z.never().optional(),
                          }),
                          /*#__PURE__*/ z.object({
                            video: /*#__PURE__*/ z.never().optional(),
                            image: /*#__PURE__*/ z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                              ),
                          }),
                          /*#__PURE__*/ z.object({
                            image: /*#__PURE__*/ z.never().optional(),
                            video: /*#__PURE__*/ z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: /*#__PURE__*/ z
                    .object({
                      items: /*#__PURE__*/ z
                        .array(
                          /*#__PURE__*/ z.intersection(
                            /*#__PURE__*/ z.object({
                              altText: /*#__PURE__*/ z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: /*#__PURE__*/ z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: /*#__PURE__*/ z
                                .enum(['IMAGE', 'VIDEO'])
                                .optional(),
                              thumbnail: /*#__PURE__*/ z
                                .object({
                                  url: /*#__PURE__*/ z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: /*#__PURE__*/ z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: /*#__PURE__*/ z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: /*#__PURE__*/ z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: /*#__PURE__*/ z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            /*#__PURE__*/ z.intersection(
                              /*#__PURE__*/ z.xor([
                                /*#__PURE__*/ z.object({
                                  _id: /*#__PURE__*/ z.never().optional(),
                                  url: /*#__PURE__*/ z.never().optional(),
                                }),
                                /*#__PURE__*/ z.object({
                                  url: /*#__PURE__*/ z.never().optional(),
                                  _id: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                /*#__PURE__*/ z.object({
                                  _id: /*#__PURE__*/ z.never().optional(),
                                  url: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              /*#__PURE__*/ z.xor([
                                /*#__PURE__*/ z.object({
                                  image: /*#__PURE__*/ z.never().optional(),
                                  video: /*#__PURE__*/ z.never().optional(),
                                }),
                                /*#__PURE__*/ z.object({
                                  video: /*#__PURE__*/ z.never().optional(),
                                  image: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                    ),
                                }),
                                /*#__PURE__*/ z.object({
                                  image: /*#__PURE__*/ z.never().optional(),
                                  video: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe('Product media items.')
                .optional(),
              seoData: /*#__PURE__*/ z
                .object({
                  tags: /*#__PURE__*/ z
                    .array(
                      /*#__PURE__*/ z.object({
                        type: /*#__PURE__*/ z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.\n\nWhich of these an API actually accepts depends on where the tag is being set: this\nlist is not a blanket guarantee across every API that reuses this message. Site SEO\nTags in particular accepts only `meta` (see `SiteSeoTags.tags`) and rejects\n`title`/`script`/`link` with a validation error even though they appear here —\ncheck the field description of the specific `tags`-typed field you\'re writing to\nfor the types it actually supports and the error it returns.\n\nLimitation: `title` and `script` (e.g. a `script` tag with `props.type`\n`"application/ld+json"` carrying structured data) are supported only when\n`custom` is unset or `false`. Setting `custom: true` on a `title` or `script`\ntag is rejected with `TAG_TYPE_NOT_ALLOWED`, because `custom: true` routes the\nwrite through the site\'s Advanced/Custom Tags list. To write structured data,\nomit `custom` (or set it to `false`).'
                          )
                          .optional(),
                        props: /*#__PURE__*/ z
                          .record(
                            /*#__PURE__*/ z.string(),
                            /*#__PURE__*/ z.any()
                          )
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: /*#__PURE__*/ z
                          .record(
                            /*#__PURE__*/ z.string(),
                            /*#__PURE__*/ z.any()
                          )
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: /*#__PURE__*/ z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: /*#__PURE__*/ z
                          .boolean()
                          .describe(
                            "Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages) —\ni.e. an entry from the site's Advanced/Custom Tags list.\n\nSetting this to `true` on a write restricts which `type` values are accepted\n— the same restriction the Advanced/Custom Tags UI enforces. `title` and\n`script` tags (including JSON-LD structured data) must be sent with `custom`\nomitted or `false`; sending them with `custom: true` is rejected with\n`TAG_TYPE_NOT_ALLOWED`.\n\nNote this is a separate restriction from Site SEO Tags' own type restriction on\n`SiteSeoTags.tags`: even with `custom` unset, Site SEO Tags rejects `script` (e.g.\nfor site-wide structured data) unconditionally. There is currently no way to set\nsite-wide, every-page structured data through any of these APIs — write a per-page\n`script` tag through the Item SEO Tags API instead.\n\nThis flag is scoped to that feature only. It is NOT an indicator of whether a standard tag\n(`title`, `description`, Open Graph, etc.) reflects a manually configured value versus a computed\ndefault/pattern — standard tags always resolve with `custom: false`, even when their content comes\nfrom a page's manually saved SEO title/description override. Don't use this field to verify whether\na live page reflects a manual per-page SEO edit.\n\nLimitation: when `ResolveStaticPageSeoTags` is called without `seoData`, the per-page SEO data is read\nonly from the Vibe/Wix-managed-headless override store. That store holds nothing for classic Wix Editor\nor Wix Studio pages, so even genuine Advanced/Custom Tags saved on those pages are missing from the\nresponse entirely — there, `false` means this API could not tell, not that no custom tag exists.\nPass `seoData` explicitly to resolve against a known set of tags."
                          )
                          .optional(),
                        disabled: /*#__PURE__*/ z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: /*#__PURE__*/ z
                    .object({
                      preventAutoRedirect: /*#__PURE__*/ z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: /*#__PURE__*/ z
                        .array(
                          /*#__PURE__*/ z.object({
                            term: /*#__PURE__*/ z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: /*#__PURE__*/ z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: /*#__PURE__*/ z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: /*#__PURE__*/ z
                .array(
                  /*#__PURE__*/ z.intersection(
                    /*#__PURE__*/ z.object({
                      _id: /*#__PURE__*/ z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: /*#__PURE__*/ z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: /*#__PURE__*/ z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      key: /*#__PURE__*/ z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    /*#__PURE__*/ z.xor([
                      /*#__PURE__*/ z.object({
                        choicesSettings: /*#__PURE__*/ z.never().optional(),
                      }),
                      /*#__PURE__*/ z.object({
                        choicesSettings: /*#__PURE__*/ z
                          .object({
                            choices: /*#__PURE__*/ z
                              .array(
                                /*#__PURE__*/ z.intersection(
                                  /*#__PURE__*/ z.object({
                                    choiceId: /*#__PURE__*/ z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: /*#__PURE__*/ z
                                      .array(
                                        /*#__PURE__*/ z.intersection(
                                          /*#__PURE__*/ z.object({
                                            altText: /*#__PURE__*/ z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: /*#__PURE__*/ z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: /*#__PURE__*/ z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: /*#__PURE__*/ z
                                              .object({
                                                url: /*#__PURE__*/ z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: /*#__PURE__*/ z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: /*#__PURE__*/ z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: /*#__PURE__*/ z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          /*#__PURE__*/ z.intersection(
                                            /*#__PURE__*/ z.xor([
                                              /*#__PURE__*/ z.object({
                                                _id: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                url: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                url: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                _id: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                _id: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                url: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            /*#__PURE__*/ z.xor([
                                              /*#__PURE__*/ z.object({
                                                image: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                video: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                video: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                image: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                                  ),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                image: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                video: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: /*#__PURE__*/ z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: /*#__PURE__*/ z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: /*#__PURE__*/ z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                    displayImage: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Image to display alongside the choice.\n\nThis field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.\nThe returned image contains only its ID, URL, and alt text.'
                                      )
                                      .optional(),
                                    media: /*#__PURE__*/ z
                                      .object({
                                        items: /*#__PURE__*/ z
                                          .array(
                                            /*#__PURE__*/ z.intersection(
                                              /*#__PURE__*/ z.object({}),
                                              /*#__PURE__*/ z.xor([
                                                /*#__PURE__*/ z.object({
                                                  mediaId: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  url: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                }),
                                                /*#__PURE__*/ z.object({
                                                  url: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  mediaId: /*#__PURE__*/ z
                                                    .string()
                                                    .describe(
                                                      'Media ID from the Wix Media Manager. Will always be returned on read.'
                                                    )
                                                    .min(1)
                                                    .max(200),
                                                }),
                                                /*#__PURE__*/ z.object({
                                                  mediaId: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  url: /*#__PURE__*/ z
                                                    .string()
                                                    .describe(
                                                      'External media URL. Can be used only on write.'
                                                    )
                                                    .url(),
                                                }),
                                              ])
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                      })
                                      .describe(
                                        'Media references for this choice.\n\nOn *write*, you can use either `media_id` or `url` to assign a media to this choice from the product\'s media.\nOn *read*, this field will return the media IDs of the associated media items.\nWhen this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.\n\n> **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.'
                                      )
                                      .optional(),
                                  }),
                                  /*#__PURE__*/ z.xor([
                                    /*#__PURE__*/ z.object({
                                      colorCode: /*#__PURE__*/ z
                                        .never()
                                        .optional(),
                                    }),
                                    /*#__PURE__*/ z.object({
                                      colorCode: /*#__PURE__*/ z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(1000)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: /*#__PURE__*/ z
                .array(
                  /*#__PURE__*/ z.intersection(
                    /*#__PURE__*/ z.object({
                      _id: /*#__PURE__*/ z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: /*#__PURE__*/ z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: /*#__PURE__*/ z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      mandatory: /*#__PURE__*/ z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: /*#__PURE__*/ z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    /*#__PURE__*/ z.xor([
                      /*#__PURE__*/ z.object({
                        freeTextSettings: /*#__PURE__*/ z.never().optional(),
                        choicesSettings: /*#__PURE__*/ z.never().optional(),
                      }),
                      /*#__PURE__*/ z.object({
                        choicesSettings: /*#__PURE__*/ z.never().optional(),
                        freeTextSettings: /*#__PURE__*/ z
                          .object({
                            minCharCount: /*#__PURE__*/ z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: /*#__PURE__*/ z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: /*#__PURE__*/ z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      /*#__PURE__*/ z.object({
                        freeTextSettings: /*#__PURE__*/ z.never().optional(),
                        choicesSettings: /*#__PURE__*/ z
                          .object({
                            choices: /*#__PURE__*/ z
                              .array(
                                /*#__PURE__*/ z.intersection(
                                  /*#__PURE__*/ z.object({
                                    choiceId: /*#__PURE__*/ z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: /*#__PURE__*/ z
                                      .array(
                                        /*#__PURE__*/ z.intersection(
                                          /*#__PURE__*/ z.object({
                                            altText: /*#__PURE__*/ z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: /*#__PURE__*/ z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: /*#__PURE__*/ z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: /*#__PURE__*/ z
                                              .object({
                                                url: /*#__PURE__*/ z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: /*#__PURE__*/ z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: /*#__PURE__*/ z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: /*#__PURE__*/ z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          /*#__PURE__*/ z.intersection(
                                            /*#__PURE__*/ z.xor([
                                              /*#__PURE__*/ z.object({
                                                _id: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                url: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                url: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                _id: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                _id: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                url: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            /*#__PURE__*/ z.xor([
                                              /*#__PURE__*/ z.object({
                                                image: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                video: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                video: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                image: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field."
                                                  ),
                                              }),
                                              /*#__PURE__*/ z.object({
                                                image: /*#__PURE__*/ z
                                                  .never()
                                                  .optional(),
                                                video: /*#__PURE__*/ z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: /*#__PURE__*/ z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: /*#__PURE__*/ z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: /*#__PURE__*/ z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                    displayImage: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Image to display alongside the choice.\n\nThis field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.\nThe returned image contains only its ID, URL, and alt text.'
                                      )
                                      .optional(),
                                    media: /*#__PURE__*/ z
                                      .object({
                                        items: /*#__PURE__*/ z
                                          .array(
                                            /*#__PURE__*/ z.intersection(
                                              /*#__PURE__*/ z.object({}),
                                              /*#__PURE__*/ z.xor([
                                                /*#__PURE__*/ z.object({
                                                  mediaId: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  url: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                }),
                                                /*#__PURE__*/ z.object({
                                                  url: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  mediaId: /*#__PURE__*/ z
                                                    .string()
                                                    .describe(
                                                      'Media ID from the Wix Media Manager. Will always be returned on read.'
                                                    )
                                                    .min(1)
                                                    .max(200),
                                                }),
                                                /*#__PURE__*/ z.object({
                                                  mediaId: /*#__PURE__*/ z
                                                    .never()
                                                    .optional(),
                                                  url: /*#__PURE__*/ z
                                                    .string()
                                                    .describe(
                                                      'External media URL. Can be used only on write.'
                                                    )
                                                    .url(),
                                                }),
                                              ])
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                      })
                                      .describe(
                                        'Media references for this choice.\n\nOn *write*, you can use either `media_id` or `url` to assign a media to this choice from the product\'s media.\nOn *read*, this field will return the media IDs of the associated media items.\nWhen this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.\n\n> **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.'
                                      )
                                      .optional(),
                                  }),
                                  /*#__PURE__*/ z.xor([
                                    /*#__PURE__*/ z.object({
                                      colorCode: /*#__PURE__*/ z
                                        .never()
                                        .optional(),
                                    }),
                                    /*#__PURE__*/ z.object({
                                      colorCode: /*#__PURE__*/ z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(1000)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: /*#__PURE__*/ z
                .object({
                  _id: /*#__PURE__*/ z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: /*#__PURE__*/ z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: /*#__PURE__*/ z
                .array(
                  /*#__PURE__*/ z.object({
                    _id: /*#__PURE__*/ z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: /*#__PURE__*/ z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: /*#__PURE__*/ z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: /*#__PURE__*/ z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: /*#__PURE__*/ z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(20)
                .optional(),
              ribbon: /*#__PURE__*/ z
                .object({
                  _id: /*#__PURE__*/ z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: /*#__PURE__*/ z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Primary product ribbon displayed on the product. Use `additionalRibbons` to assign further ribbons beyond the primary one.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              additionalRibbons: /*#__PURE__*/ z
                .array(
                  /*#__PURE__*/ z.object({
                    _id: /*#__PURE__*/ z
                      .string()
                      .describe('Ribbon ID.')
                      .min(1)
                      .max(36)
                      .optional()
                      .nullable(),
                    name: /*#__PURE__*/ z
                      .string()
                      .describe('Ribbon name.')
                      .max(30)
                      .optional()
                      .nullable(),
                  })
                )
                .max(4)
                .optional(),
              mainCategoryId: /*#__PURE__*/ z
                .string()
                .describe(
                  'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productType: /*#__PURE__*/ z
                .enum(['PHYSICAL', 'DIGITAL'])
                .optional(),
              handle: /*#__PURE__*/ z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100),
              variantsInfo: /*#__PURE__*/ z
                .object({
                  variants: /*#__PURE__*/ z
                    .array(
                      /*#__PURE__*/ z.intersection(
                        /*#__PURE__*/ z.object({
                          _id: /*#__PURE__*/ z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: /*#__PURE__*/ z
                            .boolean()
                            .describe(
                              'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          sku: /*#__PURE__*/ z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: /*#__PURE__*/ z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          price: /*#__PURE__*/ z
                            .object({
                              actualPrice: /*#__PURE__*/ z
                                .object({
                                  amount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: /*#__PURE__*/ z
                                .object({
                                  amount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                              priceAfterDiscount: /*#__PURE__*/ z
                                .object({
                                  amount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Variant selling price after automatic discounts are applied to `actualPrice`.\n\n> **Notes:**\n> + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.\n> + On Query Products and Search Products, returned only on `variantSummary.minPriceVariant` when `MIN_PRICE_VARIANT` is also requested.\n> + `formattedAmount` is returned only when you also pass `"CURRENCY"`.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: /*#__PURE__*/ z
                            .object({
                              cost: /*#__PURE__*/ z
                                .object({
                                  amount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: /*#__PURE__*/ z
                                .object({
                                  amount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: /*#__PURE__*/ z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          inventoryItem: /*#__PURE__*/ z
                            .intersection(
                              /*#__PURE__*/ z.object({
                                _id: /*#__PURE__*/ z
                                  .string()
                                  .describe(
                                    'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                revision: /*#__PURE__*/ z
                                  .string()
                                  .regex(
                                    /^-?\d+$/,
                                    'Must be a valid Int64 string'
                                  )
                                  .describe('Inventory item revision.')
                                  .optional()
                                  .nullable(),
                                preorderInfo: /*#__PURE__*/ z
                                  .object({
                                    enabled: /*#__PURE__*/ z
                                      .boolean()
                                      .describe(
                                        "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                      )
                                      .optional()
                                      .nullable(),
                                    message: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                      )
                                      .min(1)
                                      .max(250)
                                      .optional()
                                      .nullable(),
                                    limit: /*#__PURE__*/ z
                                      .number()
                                      .int()
                                      .describe(
                                        'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                      )
                                      .min(0)
                                      .max(100000)
                                      .optional()
                                      .nullable(),
                                    counter: /*#__PURE__*/ z
                                      .number()
                                      .int()
                                      .describe(
                                        'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                      )
                                      .min(0)
                                      .max(99999)
                                      .optional()
                                      .nullable(),
                                    quantity: /*#__PURE__*/ z
                                      .number()
                                      .int()
                                      .describe(
                                        'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                      )
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item preorder info.')
                                  .optional(),
                              }),
                              /*#__PURE__*/ z.xor([
                                /*#__PURE__*/ z.object({
                                  inStock: /*#__PURE__*/ z.never().optional(),
                                  quantity: /*#__PURE__*/ z.never().optional(),
                                }),
                                /*#__PURE__*/ z.object({
                                  quantity: /*#__PURE__*/ z.never().optional(),
                                  inStock: /*#__PURE__*/ z
                                    .boolean()
                                    .describe(
                                      'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                                    ),
                                }),
                                /*#__PURE__*/ z.object({
                                  inStock: /*#__PURE__*/ z.never().optional(),
                                  quantity: /*#__PURE__*/ z
                                    .number()
                                    .int()
                                    .describe(
                                      'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                                    )
                                    .min(-99999)
                                    .max(99999),
                                }),
                              ])
                            )
                            .describe(
                              'Inventory item of the variant on the default location.'
                            )
                            .optional(),
                          choices: /*#__PURE__*/ z
                            .array(
                              /*#__PURE__*/ z.object({
                                optionChoiceIds: /*#__PURE__*/ z
                                  .object({
                                    optionId: /*#__PURE__*/ z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: /*#__PURE__*/ z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe('Option and choice IDs.')
                                  .optional(),
                                optionChoiceNames: /*#__PURE__*/ z
                                  .object({
                                    optionName: /*#__PURE__*/ z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: /*#__PURE__*/ z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: /*#__PURE__*/ z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .optional(),
                                  })
                                  .describe('Option and choice names.')
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                        }),
                        /*#__PURE__*/ z.xor([
                          /*#__PURE__*/ z.object({
                            physicalProperties: /*#__PURE__*/ z
                              .never()
                              .optional(),
                            digitalProperties: /*#__PURE__*/ z
                              .never()
                              .optional(),
                          }),
                          /*#__PURE__*/ z.object({
                            digitalProperties: /*#__PURE__*/ z
                              .never()
                              .optional(),
                            physicalProperties: /*#__PURE__*/ z
                              .object({
                                weight: /*#__PURE__*/ z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: /*#__PURE__*/ z
                                  .object({
                                    settings: /*#__PURE__*/ z
                                      .object({
                                        quantity: /*#__PURE__*/ z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: /*#__PURE__*/ z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                    priceAfterDiscount: /*#__PURE__*/ z
                                      .object({
                                        amount: /*#__PURE__*/ z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: /*#__PURE__*/ z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Price per unit calculated from the variant automatic discount price.\n\n> **Notes:**\n> + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.\n> + `formattedAmount` is returned only when you also pass `"CURRENCY"`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: /*#__PURE__*/ z
                                  .object({
                                    length: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: /*#__PURE__*/ z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: /*#__PURE__*/ z
                                  .object({
                                    length: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: /*#__PURE__*/ z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: /*#__PURE__*/ z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          /*#__PURE__*/ z.object({
                            physicalProperties: /*#__PURE__*/ z
                              .never()
                              .optional(),
                            digitalProperties: /*#__PURE__*/ z
                              .object({
                                digitalFile: /*#__PURE__*/ z
                                  .object({
                                    _id: /*#__PURE__*/ z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: /*#__PURE__*/ z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: /*#__PURE__*/ z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: /*#__PURE__*/ z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
                )
                .optional(),
              subscriptionDetails: /*#__PURE__*/ z
                .object({
                  subscriptions: /*#__PURE__*/ z
                    .array(
                      /*#__PURE__*/ z.intersection(
                        /*#__PURE__*/ z.object({
                          _id: /*#__PURE__*/ z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: /*#__PURE__*/ z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: /*#__PURE__*/ z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: /*#__PURE__*/ z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: /*#__PURE__*/ z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .optional(),
                          interval: /*#__PURE__*/ z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: /*#__PURE__*/ z
                            .intersection(
                              /*#__PURE__*/ z.object({
                                type: /*#__PURE__*/ z
                                  .enum(['AMOUNT', 'PERCENT'])
                                  .optional(),
                              }),
                              /*#__PURE__*/ z.xor([
                                /*#__PURE__*/ z.object({
                                  amountOff: /*#__PURE__*/ z.never().optional(),
                                  percentOff: /*#__PURE__*/ z
                                    .never()
                                    .optional(),
                                }),
                                /*#__PURE__*/ z.object({
                                  percentOff: /*#__PURE__*/ z
                                    .never()
                                    .optional(),
                                  amountOff: /*#__PURE__*/ z
                                    .string()
                                    .describe(
                                      'Fixed amount subtracted from the variant price when calculating subscription prices.'
                                    ),
                                }),
                                /*#__PURE__*/ z.object({
                                  amountOff: /*#__PURE__*/ z.never().optional(),
                                  percentOff: /*#__PURE__*/ z
                                    .number()
                                    .describe(
                                      'Percentage subtracted from the variant price when calculating subscription prices.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        /*#__PURE__*/ z.xor([
                          /*#__PURE__*/ z.object({
                            autoRenewal: /*#__PURE__*/ z.never().optional(),
                            billingCycles: /*#__PURE__*/ z.never().optional(),
                          }),
                          /*#__PURE__*/ z.object({
                            billingCycles: /*#__PURE__*/ z.never().optional(),
                            autoRenewal: /*#__PURE__*/ z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          /*#__PURE__*/ z.object({
                            autoRenewal: /*#__PURE__*/ z.never().optional(),
                            billingCycles: /*#__PURE__*/ z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: /*#__PURE__*/ z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              purchaseEligibility: /*#__PURE__*/ z
                .object({
                  userCriteria: /*#__PURE__*/ z
                    .enum(['MEMBERS_ONLY'])
                    .optional(),
                })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: /*#__PURE__*/ z
                .object({
                  namespaces: /*#__PURE__*/ z
                    .record(
                      /*#__PURE__*/ z.string(),
                      /*#__PURE__*/ z.record(
                        /*#__PURE__*/ z.string(),
                        /*#__PURE__*/ z.any()
                      )
                    )
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
                )
                .optional(),
              tags: /*#__PURE__*/ z
                .object({
                  privateTags: /*#__PURE__*/ z
                    .object({
                      tagIds: /*#__PURE__*/ z
                        .array(/*#__PURE__*/ z.string())
                        .max(100)
                        .optional(),
                    })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: /*#__PURE__*/ z
                    .object({
                      tagIds: /*#__PURE__*/ z
                        .array(/*#__PURE__*/ z.string())
                        .max(100)
                        .optional(),
                    })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
            }),
            /*#__PURE__*/ z.xor([
              /*#__PURE__*/ z.object({
                physicalProperties: /*#__PURE__*/ z.never().optional(),
              }),
              /*#__PURE__*/ z.object({
                physicalProperties: /*#__PURE__*/ z
                  .object({
                    pricePerUnit: /*#__PURE__*/ z
                      .object({
                        quantity: /*#__PURE__*/ z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: /*#__PURE__*/ z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: /*#__PURE__*/ z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: /*#__PURE__*/ z
                      .object({
                        minValue: /*#__PURE__*/ z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: /*#__PURE__*/ z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: /*#__PURE__*/ z
                      .object({
                        minValue: /*#__PURE__*/ z
                          .object({
                            value: /*#__PURE__*/ z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: /*#__PURE__*/ z
                          .object({
                            value: /*#__PURE__*/ z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: /*#__PURE__*/ z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: /*#__PURE__*/ z
                      .object({
                        weightMeasurementUnit: /*#__PURE__*/ z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: /*#__PURE__*/ z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Product to upsert - handle field is required for identification'
          ),
        categoryIdsInfo: /*#__PURE__*/ z
          .object({
            categoryIds: /*#__PURE__*/ z
              .array(/*#__PURE__*/ z.string())
              .max(1000)
              .optional(),
          })
          .describe(
            'Categories to set for the product.\nOverrides any existing category assignments.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
});
export const AddProductsToImportResponse = /*#__PURE__*/ z.object({
  results: /*#__PURE__*/ z
    .array(
      /*#__PURE__*/ z.object({
        itemMetadata: /*#__PURE__*/ z
          .object({
            _id: /*#__PURE__*/ z
              .string()
              .describe(
                'The product `handle` as passed in the `AddProductsToImport` request.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: /*#__PURE__*/ z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: /*#__PURE__*/ z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: /*#__PURE__*/ z
              .object({
                code: /*#__PURE__*/ z
                  .string()
                  .describe('Error code.')
                  .optional(),
                description: /*#__PURE__*/ z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: /*#__PURE__*/ z
                  .record(/*#__PURE__*/ z.string(), /*#__PURE__*/ z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: /*#__PURE__*/ z
    .object({
      totalSuccesses: /*#__PURE__*/ z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: /*#__PURE__*/ z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: /*#__PURE__*/ z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const SubmitCatalogImportRequest = /*#__PURE__*/ z.object({
  catalogImportId: /*#__PURE__*/ z
    .string()
    .describe('Catalog Import ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SubmitCatalogImportResponse = /*#__PURE__*/ z.object({
  jobId: /*#__PURE__*/ z
    .string()
    .describe(
      'Job ID for async operation tracking. Pass to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) or [List Async Job Items](https://dev.wix.com/docs/api-reference/business-management/async-job/list-async-job-items) to retrieve details and metadata'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  totalProductsCount: /*#__PURE__*/ z
    .number()
    .int()
    .describe('Total number of items that will be processed in this import')
    .optional(),
});
